import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  CheckCircle,
  User,
  Phone,
  Calendar,
  Image as ImageIcon,
} from "lucide-react";
import Header from "./Header";
import AppButton from "../shared/AppButton.jsx";
import { API_BASE_URL } from "../../config";
import { formatDateOnly } from "../utils/formatDate";

const QueriesPage = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const queriesPerPage = 9;

  useEffect(() => {
    const token = window.__appSession.getToken();

    const fetchQueries = async () => {
      const apiUrl = `${API_BASE_URL}/api/raise-query/queries`;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      try {
        const response = await fetch(apiUrl, { headers });
        const data = await response.json();

        if (data.queries) {
          setQueries(data.queries);
        } else {
          setError("No queries found.");
        }
      } catch (err) {
        setError("Failed to load queries. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchQueries();
  }, []);

  const toggleStatus = async (id, currentStatus) => {
    const token = window.__appSession.getToken();
    const newStatus = currentStatus === "pending" ? "resolved" : "pending";

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/raise-query/queries/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (response.ok) {
        setQueries((prevQueries) =>
          prevQueries.map((query) =>
            query._id === id ? { ...query, status: newStatus } : query
          )
        );
      } else {
        console.error("Error updating query status");
      }
    } catch (error) {
      console.error("Error updating query status:", error);
    }
  };

  const indexOfLastQuery = currentPage * queriesPerPage;
  const indexOfFirstQuery = indexOfLastQuery - queriesPerPage;
  const currentQueries = queries.slice(indexOfFirstQuery, indexOfLastQuery);
  const totalPages = Math.ceil(queries.length / queriesPerPage);
  const pendingCount = queries.filter((query) => query.status === "pending").length;
  const resolvedCount = queries.length - pendingCount;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Header
      pageTitle="User Queries Management"
      pageSubtitle="Manage and respond to user support requests"
    >
      <div className="space-y-3 p-1">
        <motion.div
          className="glass-surface-strong rounded-2xl border border-indigo-200/70 p-3"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-700 bg-clip-text text-xl font-bold text-transparent">
                User Queries Management
              </h1>
              <p className="mt-0.5 text-xs text-slate-600">
                Review incoming support requests and keep the queue healthy.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:w-[360px]">
              <div className="rounded-xl border border-indigo-200 bg-white/80 px-2 py-1.5 text-center">
                <p className="text-[10px] text-slate-500">Total</p>
                <p className="text-sm font-bold text-slate-900">{queries.length}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-amber-700">Pending</p>
                <p className="text-sm font-bold text-amber-700">{pendingCount}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-emerald-700">Resolved</p>
                <p className="text-sm font-bold text-emerald-700">{resolvedCount}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="h-10 w-10 animate-spin rounded-full border-8 border-indigo-100 border-t-4 border-t-indigo-600" />
          </div>
        ) : error ? (
          <motion.div
            className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="text-red-600 text-sm">{error}</p>
          </motion.div>
        ) : currentQueries.length === 0 ? (
          <motion.div
            className="glass-surface rounded-xl border border-indigo-200 p-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="text-slate-500 text-sm">No queries found.</p>
          </motion.div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {currentQueries.map((query, index) => (
              <motion.div
                key={query._id}
                className="glass-surface overflow-hidden rounded-2xl border border-indigo-200/70 transition-shadow duration-300 hover:shadow-[0_20px_38px_-26px_rgba(79,70,229,0.72)]"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="border-b border-indigo-100 p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
                      <User className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {query.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-xs text-slate-500">
                        <div className="flex items-center space-x-1">
                          <Phone className="h-3 w-3" />
                          <span>{query.phone}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDateOnly(query.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        query.status === "pending"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {query.status === "pending" ? (
                        <Clock className="h-3 w-3 mr-1" />
                      ) : (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      )}
                      {query.status}
                    </span>
                  </div>
                </div>

                <div className="p-3">
                  <h4 className="mb-1 text-xs font-semibold text-slate-900">
                    Problem Description:
                  </h4>
                  <p className="mb-2 text-xs leading-snug text-slate-600">
                    {query.problem}
                  </p>

                  {query.images && query.images.length > 0 && (
                    <div className="mb-2">
                      <div className="flex items-center space-x-1 mb-1">
                        <ImageIcon className="h-3 w-3 text-slate-500" />
                        <span className="text-xs font-medium text-slate-700">
                          Attachments ({query.images.length})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {query.images.slice(0, 4).map((imgUrl, imgIndex) => (
                          <div key={imgIndex} className="relative group">
                            <img
                              className="h-12 w-full rounded-lg border border-indigo-200 object-cover"
                              src={imgUrl}
                              alt={`Query Image ${imgIndex + 1}`}
                            />
                            {query.images.length > 4 && imgIndex === 3 && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs font-medium">
                                  +{query.images.length - 4} more
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <AppButton
                    onClick={() => toggleStatus(query._id, query.status)}
                    size="sm"
                    variant={query.status === "pending" ? "submit" : "success"}
                    className="w-full"
                  >
                    {query.status === "pending"
                      ? "Mark as Resolved"
                      : "Mark as Pending"}
                  </AppButton>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {queries.length > queriesPerPage && (
          <motion.div
            className="mt-4 flex items-center justify-center space-x-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <AppButton
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              size="sm"
              variant="clear"
              className="gap-1"
            >
              <span>Previous</span>
            </AppButton>

            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = index + 1;
                } else if (currentPage <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + index;
                } else {
                  pageNumber = currentPage - 2 + index;
                }

                return (
                  <AppButton
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    size="sm"
                    variant={currentPage === pageNumber ? "submit" : "clear"}
                    className={`min-w-9 ${
                      currentPage === pageNumber
                        ? ""
                        : ""
                    }`}
                  >
                    {pageNumber}
                  </AppButton>
                );
              })}
            </div>

            <AppButton
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              size="sm"
              variant="clear"
              className="gap-1"
            >
              <span>Next</span>
            </AppButton>
          </motion.div>
        )}
      </div>
    </Header>
  );
};

export default QueriesPage;

// import React, { useState, useEffect } from "react";
// import Header from "./Header";
// import { API_BASE_URL } from "../../config";

// const QueriesPage = () => {
//   const [queries, setQueries] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const queriesPerPage = 10; // Adjust this to whatever number of queries you want per page

//   useEffect(() => {
//     const token = window.__appSession.getToken();

//     // Fetch queries data from the API
//     const fetchQueries = async () => {
//       const apiUrl = `${API_BASE_URL}/api/raise-query/queries`;
//       const headers = {
//         Authorization: `Bearer ${token}`,
//         "Content-Type": "application/json",
//       };

//       try {
//         const response = await fetch(apiUrl, { headers });
//         const data = await response.json();

//         if (data.queries) {
//           setQueries(data.queries);
//         } else {
//           setError("No queries found.");
//         }
//       } catch (err) {
//         setError("Failed to load queries. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchQueries();
//   }, []);

//   // Function to toggle the query status
//   const toggleStatus = async (id, currentStatus) => {
//     const token = window.__appSession.getToken();
//     const newStatus = currentStatus === "pending" ? "resolved" : "pending";

//     const response = await fetch(
//       `${API_BASE_URL}/api/raise-query/queries/${id}`,
//       {
//         method: "PATCH",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ status: newStatus }),
//       }
//     );

//     if (response.ok) {
//       setQueries((prevQueries) =>
//         prevQueries.map((query) =>
//           query._id === id ? { ...query, status: newStatus } : query
//         )
//       );
//     } else {
//       console.error("Error updating query status");
//     }
//   };

//   // Pagination logic
//   const indexOfLastQuery = currentPage * queriesPerPage;
//   const indexOfFirstQuery = indexOfLastQuery - queriesPerPage;
//   const currentQueries = queries.slice(indexOfFirstQuery, indexOfLastQuery);

//   // Change page
//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   // Calculate total pages
//   const totalPages = Math.ceil(queries.length / queriesPerPage);

//   return (
//     <div className="bg-gray-50 font-sans text-gray-800 min-h-screen">
//       <Header />
//       <div className="container mx-auto p-6 space-y-8">
//         <h1 className="text-4xl font-bold text-center text-indigo-600">
//           User Queries
//         </h1>

//         {loading ? (
//           <div className="flex justify-center items-center min-h-[200px]">
//             <div className="animate-spin rounded-full border-t-4 border-indigo-500 border-8 h-16 w-16"></div>
//           </div>
//         ) : error ? (
//           <p className="text-center text-red-500">{error}</p>
//         ) : currentQueries.length === 0 ? (
//           <p className="text-center text-gray-500">No queries found.</p>
//         ) : (
//           <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
//             {currentQueries.map((query) => (
//               <div
//                 key={query._id}
//                 className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out"
//               >
//                 <div className="flex items-center mb-4">
//                   <div className="w-14 h-14 rounded-full bg-gray-300">
//                     <img
//                       className="w-full h-full rounded-full object-cover"
//                       src={`https://www.gravatar.com/avatar/${query.userEmail}`}
//                       alt={query.name}
//                     />
//                   </div>
//                   <div className="ml-4">
//                     <h2 className="text-2xl font-semibold text-indigo-700">
//                       {query.name}
//                     </h2>
//                     <p className="text-sm text-gray-500">{query.phone}</p>
//                     <p className="text-xs text-gray-400">
//                       {new Date(query.createdAt).toLocaleDateString()}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="mb-4">
//                   <h3 className="text-lg font-semibold text-gray-800">
//                     Problem:
//                   </h3>
//                   <p className="text-gray-600">{query.problem}</p>
//                 </div>

//                 <div className="grid grid-cols-2 gap-2 mb-4">
//                   {query.images.map((imgUrl, index) => (
//                     <img
//                       key={index}
//                       className="w-full h-32 object-cover rounded-lg shadow-md"
//                       src={imgUrl}
//                       alt={`Query Image ${index + 1}`}
//                     />
//                   ))}
//                 </div>

//                 {/* Status toggle button */}
//                 <div className="mt-4">
//                   <p className="text-sm text-gray-600">
//                     Status:{" "}
//                     <strong
//                       className={`text-${
//                         query.status === "pending" ? "yellow" : "green"
//                       }-500`}
//                     >
//                       {query.status}
//                     </strong>
//                   </p>
//                   <button
//                     onClick={() => toggleStatus(query._id, query.status)}
//                     className={`mt-2 px-4 py-2 rounded-md transition-colors duration-200 ease-in-out ${
//                       query.status === "pending"
//                         ? "bg-yellow-500 hover:bg-yellow-600"
//                         : "bg-green-500 hover:bg-green-600"
//                     } text-white`}
//                   >
//                     {query.status === "pending"
//                       ? "Mark as Resolved"
//                       : "Mark as Pending"}
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {/* Pagination Controls */}
//         {queries.length > queriesPerPage && (
//           <div className="flex justify-center mt-8">
//             <nav>
//               <ul className="flex space-x-4">
//                 {Array.from({ length: totalPages }, (_, index) => (
//                   <li key={index}>
//                     <button
//                       onClick={() => paginate(index + 1)}
//                       className={`px-4 py-2 rounded-md ${
//                         currentPage === index + 1
//                           ? "bg-indigo-600 text-white"
//                           : "bg-gray-200 text-gray-800"
//                       } hover:bg-indigo-500 hover:text-white`}
//                     >
//                       {index + 1}
//                     </button>
//                   </li>
//                 ))}
//               </ul>
//             </nav>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default QueriesPage;
